import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, Clock, CheckCircle, XCircle, Flame,
  ChevronLeft, ChevronRight, Trophy, Zap, Loader2
} from "lucide-react";

const BankingDailyQuizPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();

  const [phase, setPhase] = useState("loading");
  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);
  const [results, setResults] = useState(null);
  const [alreadyDone, setAlreadyDone] = useState(null);

  useEffect(() => { loadQuiz(); }, []);

  useEffect(() => {
    if (phase === "quiz" && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]);

  const loadQuiz = async () => {
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/daily-quiz`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.already_attempted) {
        setAlreadyDone(data);
        setPhase("already_done");
      } else {
        setQuestions(data.questions || []);
        setTimeLeft((data.time_minutes || 5) * 60);
        setPhase("quiz");
      }
    } catch { toast({ variant: "destructive", description: "Failed to load daily quiz" }); setPhase("error"); }
  };

  const handleSubmit = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/daily-quiz/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answers, questions })
      });
      const data = await res.json();
      setResults(data);
      setPhase("results");
    } catch { toast({ variant: "destructive", description: "Failed to submit" }); }
    finally { setLoading(false); }
  };

  const formatTime = (secs) => `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, '0')}`;
  const currentQuestion = questions[currentQ];

  if (phase === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (phase === "already_done") {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-lg mx-auto px-6 py-16 text-center space-y-6">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Today's Quiz Complete!</h1>
          <p className="text-slate-600">You scored <strong className="text-emerald-600">{alreadyDone.score}/{alreadyDone.total}</strong> today</p>
          <p className="text-sm text-slate-500">Come back tomorrow for a new set of questions</p>
          <Button onClick={() => navigate("/banking")} data-testid="daily-quiz-back">Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (phase === "quiz" && currentQuestion) {
    const attempted = Object.keys(answers).length;
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-50 px-4 py-3 flex items-center justify-between border-b bg-white">
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700">Daily Quiz</Badge>
            <span className="text-xs text-slate-500">Q{currentQ + 1}/{questions.length}</span>
          </div>
          <div className="font-mono text-lg font-bold text-slate-800 flex items-center gap-2">
            <Clock className="w-4 h-4" /> {formatTime(timeLeft)}
          </div>
          <Button size="sm" onClick={handleSubmit} disabled={loading} data-testid="submit-daily-quiz">
            Submit ({attempted}/{questions.length})
          </Button>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-6">
          <Card>
            <CardContent className="p-6">
              <p className="text-lg text-slate-900 leading-relaxed mb-6">{currentQuestion.question}</p>
              <div className="space-y-3">
                {currentQuestion.options?.map((option) => {
                  const optKey = typeof option === 'object' ? option.key : option;
                  const optText = typeof option === 'object' ? option.text : option;
                  const isSelected = answers[currentQuestion.id] === optKey;
                  return (
                    <button key={optKey} onClick={() => setAnswers({ ...answers, [currentQuestion.id]: optKey })}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${isSelected ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-emerald-300"}`}
                      data-testid={`daily-option-${optKey}`}>
                      <span className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isSelected ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>{optKey.toUpperCase()}</span>
                        <span>{optText}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-between mt-6 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.max(0, currentQ - 1))} disabled={currentQ === 0}>
                  <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                </Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentQ(Math.min(questions.length - 1, currentQ + 1))} disabled={currentQ === questions.length - 1}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (phase === "results" && results) {
    const percent = results.total > 0 ? Math.round((results.score / results.total) * 100) : 0;
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-lg mx-auto px-6 py-12 space-y-6">
          <div className="text-center space-y-4">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto ${percent >= 70 ? 'bg-emerald-100' : percent >= 40 ? 'bg-amber-100' : 'bg-red-100'}`}>
              <Trophy className={`w-12 h-12 ${percent >= 70 ? 'text-emerald-600' : percent >= 40 ? 'text-amber-600' : 'text-red-600'}`} />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">{results.score}/{results.total}</h1>
            <p className="text-slate-600">{percent >= 70 ? 'Excellent!' : percent >= 40 ? 'Good effort!' : 'Keep practicing!'}</p>
            {results.streak > 0 && (
              <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
                <Flame className="w-4 h-4 mr-1" /> {results.streak} day streak
              </Badge>
            )}
          </div>

          <Card>
            <CardContent className="p-4 space-y-2">
              {results.results?.map((r, i) => (
                <div key={i} className={`flex items-center gap-3 p-2 rounded-lg text-sm ${r.is_correct ? 'bg-green-50' : 'bg-red-50'}`}>
                  {r.is_correct ? <CheckCircle className="w-4 h-4 text-green-600 shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 shrink-0" />}
                  <span className="text-slate-700 flex-1">Q{i + 1}</span>
                  {!r.is_correct && <span className="text-xs text-red-600">Ans: {r.correct_answer?.toUpperCase()}</span>}
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/banking")} data-testid="daily-quiz-dashboard">Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BankingDailyQuizPage;
